import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ApiError } from './api'
import type { PayoutOverviewStatus } from './invoice-types'
import { payoutApi } from './payout-api'

export const payoutKeys = {
  overview: (status: PayoutOverviewStatus) => ['payouts', 'overview', status] as const,
  comments: (lineId: string) => ['payouts', 'comments', lineId] as const,
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback
}

export function usePayoutOverview(status: PayoutOverviewStatus, enabled = true) {
  return useQuery({ queryKey: payoutKeys.overview(status), queryFn: () => payoutApi.overview(status), enabled })
}

function useInvalidatePayouts() {
  const queryClient = useQueryClient()
  return () => {
    queryClient.invalidateQueries({ queryKey: ['payouts'] })
    queryClient.invalidateQueries({ queryKey: ['invoices'] })
  }
}

export function useUploadIncomingInvoice() {
  const invalidate = useInvalidatePayouts()
  return useMutation({
    mutationFn: ({
      lineId,
      ...fields
    }: {
      lineId: string
      file: File
      incomingInvoiceNumber: string
      incomingInvoiceAmount: string
      incomingInvoiceTotal: string
      incomingInvoiceDifferenceReason?: string
    }) => payoutApi.uploadIncomingInvoice(lineId, fields),
    onSuccess: () => {
      invalidate()
      toast.success('Inkoopfactuur gekoppeld')
    },
    onError: (e) => toast.error(errorMessage(e, 'Uploaden mislukt')),
  })
}

export function useRemoveIncomingInvoice() {
  const invalidate = useInvalidatePayouts()
  return useMutation({
    mutationFn: (lineId: string) => payoutApi.removeIncomingInvoice(lineId),
    onSuccess: () => {
      invalidate()
      toast.success('Inkoopfactuur verwijderd')
    },
    onError: (e) => toast.error(errorMessage(e, 'Verwijderen mislukt')),
  })
}

export function useApproveIncomingInvoice() {
  const invalidate = useInvalidatePayouts()
  return useMutation({
    mutationFn: ({ lineId, approve }: { lineId: string; approve: boolean }) => payoutApi.approveIncomingInvoice(lineId, approve),
    onSuccess: (_data, { approve }) => {
      invalidate()
      toast.success(approve ? 'Inkoopfactuur geaccordeerd' : 'Accordering ingetrokken')
    },
    onError: (e) => toast.error(errorMessage(e, 'Actie mislukt')),
  })
}

export function useDeclineIncomingInvoice() {
  const invalidate = useInvalidatePayouts()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ lineId, reason }: { lineId: string; reason: string }) => payoutApi.declineIncomingInvoice(lineId, reason),
    onSuccess: (_data, { lineId }) => {
      invalidate()
      queryClient.invalidateQueries({ queryKey: payoutKeys.comments(lineId) })
      toast.success('Inkoopfactuur afgekeurd')
    },
    onError: (e) => toast.error(errorMessage(e, 'Afkeuren mislukt')),
  })
}

export function useUndoDeclineIncomingInvoice() {
  const invalidate = useInvalidatePayouts()
  return useMutation({
    mutationFn: (lineId: string) => payoutApi.undoDeclineIncomingInvoice(lineId),
    onSuccess: () => {
      invalidate()
      toast.success('Afkeuring ingetrokken')
    },
    onError: (e) => toast.error(errorMessage(e, 'Intrekken mislukt')),
  })
}

export function usePayoutComments(lineId: string, enabled: boolean) {
  return useQuery({ queryKey: payoutKeys.comments(lineId), queryFn: () => payoutApi.comments(lineId), enabled })
}

export function useAddPayoutComment(lineId: string) {
  const invalidate = useInvalidatePayouts()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (text: string) => payoutApi.addComment(lineId, text),
    onSuccess: (comments) => {
      queryClient.setQueryData(payoutKeys.comments(lineId), comments)
      invalidate()
    },
    onError: (e) => toast.error(errorMessage(e, 'Reactie plaatsen mislukt')),
  })
}

export function useUpdatePayoutComment(lineId: string) {
  const invalidate = useInvalidatePayouts()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ commentId, text }: { commentId: string; text: string }) =>
      payoutApi.updateComment(lineId, commentId, text),
    onSuccess: (comments) => {
      queryClient.setQueryData(payoutKeys.comments(lineId), comments)
      invalidate()
    },
    onError: (e) => toast.error(errorMessage(e, 'Reactie aanpassen mislukt')),
  })
}

export function useDeletePayoutComment(lineId: string) {
  const invalidate = useInvalidatePayouts()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (commentId: string) => payoutApi.deleteComment(lineId, commentId),
    onSuccess: (comments) => {
      queryClient.setQueryData(payoutKeys.comments(lineId), comments)
      invalidate()
      toast.success('Reactie verwijderd')
    },
    onError: (e) => toast.error(errorMessage(e, 'Reactie verwijderen mislukt')),
  })
}
